/**
 * MusicQueuePanel - フルスクリーンプレイヤーの「次に再生」タブ
 *
 * 履歴・再生中の曲・シャッフル / リピート・次に再生を 1 つのスクロール領域に並べ、
 * 初期表示では再生中の曲が先頭に来るようスクロール位置を合わせる（上に引くと履歴が見える）。
 */

import type { DragEndEvent } from '@dnd-kit/core'
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, ListX, Music, Repeat, Repeat1, Shuffle, X } from 'lucide-react'
import { useLayoutEffect, useMemo, useRef } from 'react'

import { AuthImage } from '@/components/ui/auth-image'
import { cn } from '@/lib/utils'
import { useMusicPlayerStore } from '@/store/useMusicPlayerStore'
import { type MusicTrack, useMusicStore } from '@/store/useMusicStore'

/** シャッフル / リピートのトグルボタン共通スタイル */
const TOGGLE_PILL_CLASS = 'flex h-11 flex-1 items-center justify-center rounded-full border-none transition-colors'

/** 履歴に表示する件数 */
const HISTORY_DISPLAY_LIMIT = 30

type TrackArtworkProps = {
  thumbnail: string | null
  title: string
  productId?: number
  size: number
}

const TrackArtwork = ({ thumbnail, title, productId, size }: TrackArtworkProps) => (
  <div className="shrink-0 overflow-hidden rounded-sm bg-white/10" style={{ width: size, height: size }}>
    {thumbnail ? (
      <AuthImage
        src={thumbnail}
        alt={title}
        className="size-full object-cover"
        cacheKey={productId === undefined ? undefined : `music-album-${productId}`}
      />
    ) : (
      <div className="flex size-full items-center justify-center">
        <Music size={size / 2.5} className="text-muted-foreground" />
      </div>
    )}
  </div>
)

type SortableTrackProps = {
  track: MusicTrack
  onPlay: () => void
  onRemove: () => void
}

const SortableTrack = ({ track, onPlay, onRemove }: SortableTrackProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: track.episodeId,
  })

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn('flex items-center gap-3', isDragging && 'opacity-50')}
    >
      <TrackArtwork thumbnail={track.thumbnail} title={track.title} productId={track.productId} size={44} />
      <button
        type="button"
        onClick={onPlay}
        className="flex min-w-0 flex-1 flex-col items-start border-none py-3 text-left"
      >
        <span className="w-full truncate text-sm">{track.title}</span>
        {track.artist && <span className="w-full truncate text-xs text-muted-foreground">{track.artist}</span>}
      </button>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`${track.title} をキューから削除`}
        className="shrink-0 border-none p-1 text-muted-foreground"
      >
        <X size={18} />
      </button>
      <button
        type="button"
        aria-label={`${track.title} を並べ替え`}
        className="shrink-0 cursor-grab touch-none border-none p-1 text-muted-foreground"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={18} />
      </button>
    </li>
  )
}

export const MusicQueuePanel = ({ header }: { header?: React.ReactNode }) => {
  const queue = useMusicStore((s) => s.queue)
  const currentIndex = useMusicStore((s) => s.currentIndex)
  const shuffle = useMusicStore((s) => s.shuffle)
  const repeatMode = useMusicStore((s) => s.repeatMode)
  const playAt = useMusicStore((s) => s.playAt)
  const reorderQueue = useMusicStore((s) => s.reorderQueue)
  const removeFromQueue = useMusicStore((s) => s.removeFromQueue)
  const clearUpcoming = useMusicStore((s) => s.clearUpcoming)
  const toggleShuffle = useMusicStore((s) => s.toggleShuffle)
  const cycleRepeatMode = useMusicStore((s) => s.cycleRepeatMode)
  const playHistory = useMusicPlayerStore((s) => s.playHistory)
  const clearHistory = useMusicPlayerStore((s) => s.clearHistory)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))
  const scrollRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)

  const currentTrack = queue.at(currentIndex)
  const upcoming = queue.slice(currentIndex + 1)
  const RepeatIcon = repeatMode === 'one' ? Repeat1 : Repeat

  // 履歴からタップしたときにキュー内の位置へ飛べるようにする
  const queueIndexByEpisodeId = useMemo(() => new Map(queue.map((t, i) => [t.episodeId, i])), [queue])
  // 古い順に並べ、直前に再生した曲が再生中の曲のすぐ上に来るようにする
  const history = useMemo(
    () =>
      playHistory
        .filter((entry) => entry.episodeId !== currentTrack?.episodeId)
        .slice(0, HISTORY_DISPLAY_LIMIT)
        .reverse(),
    [playHistory, currentTrack?.episodeId],
  )

  // 初期表示では再生中の曲を先頭に合わせ、上にスクロールすると履歴が現れる
  useLayoutEffect(() => {
    const scroller = scrollRef.current
    const headerEl = headerRef.current
    if (!scroller || !headerEl) return
    scroller.scrollTop = headerEl.offsetTop
  }, [])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const from = queue.findIndex((t) => t.episodeId === active.id)
    const to = queue.findIndex((t) => t.episodeId === over.id)
    if (from < 0 || to < 0) return
    reorderQueue(from, to)
  }

  return (
    <div ref={scrollRef} className="relative h-full overflow-y-auto">
      <div className="flex flex-col gap-4">
        {history.length > 0 && (
          <>
            <div className="flex items-end justify-between gap-2">
              <div className="text-lg font-bold">履歴</div>
              <button
                type="button"
                onClick={clearHistory}
                className="flex shrink-0 items-center gap-1 border-none text-xs text-muted-foreground"
              >
                <ListX size={14} />
                消去
              </button>
            </div>

            <ul className="flex flex-col">
              {history.map((entry) => {
                const queueIndex = queueIndexByEpisodeId.get(entry.episodeId)
                return (
                  <li key={entry.episodeId} className="flex items-center gap-3">
                    <TrackArtwork
                      thumbnail={entry.thumbnailPath}
                      title={entry.title}
                      productId={entry.productId}
                      size={44}
                    />
                    <button
                      type="button"
                      disabled={queueIndex === undefined}
                      onClick={() => queueIndex !== undefined && playAt(queueIndex)}
                      className="flex min-w-0 flex-1 flex-col items-start border-none py-3 text-left disabled:cursor-default"
                    >
                      <span className="w-full truncate text-sm text-muted-foreground">{entry.title}</span>
                      {entry.artist && (
                        <span className="w-full truncate text-xs text-muted-foreground">{entry.artist}</span>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          </>
        )}

        <div ref={headerRef}>{header}</div>

        <div className="flex gap-3">
          <button
            type="button"
            aria-label="シャッフル"
            aria-pressed={shuffle}
            onClick={toggleShuffle}
            className={cn(TOGGLE_PILL_CLASS, shuffle ? 'bg-white text-black' : 'bg-white/10 text-muted-foreground')}
          >
            <Shuffle size={20} />
          </button>
          <button
            type="button"
            aria-label={repeatMode === 'one' ? '1曲リピート' : 'リピート'}
            aria-pressed={repeatMode !== 'off'}
            onClick={cycleRepeatMode}
            className={cn(
              TOGGLE_PILL_CLASS,
              repeatMode !== 'off' ? 'bg-white text-black' : 'bg-white/10 text-muted-foreground',
            )}
          >
            <RepeatIcon size={20} />
          </button>
        </div>

        <div className="flex items-end justify-between gap-2">
          <div className="min-w-0">
            <div className="text-lg font-bold">再生を続ける</div>
            {currentTrack?.album && (
              <div className="truncate text-sm text-muted-foreground">再生元: {currentTrack.album}</div>
            )}
          </div>
          {upcoming.length > 0 && (
            <button
              type="button"
              onClick={clearUpcoming}
              className="flex shrink-0 items-center gap-1 border-none text-xs text-muted-foreground"
            >
              <ListX size={14} />
              クリア
            </button>
          )}
        </div>

        {upcoming.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">次に再生する曲はありません</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={upcoming.map((t) => t.episodeId)} strategy={verticalListSortingStrategy}>
              <ul className="flex flex-col">
                {upcoming.map((track, i) => {
                  const queueIndex = currentIndex + 1 + i
                  return (
                    <SortableTrack
                      key={track.episodeId}
                      track={track}
                      onPlay={() => playAt(queueIndex)}
                      onRemove={() => removeFromQueue(queueIndex)}
                    />
                  )
                })}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  )
}
